<template>
  <!-- Förhandsvisa en enskild slide med samma design som huvudpresentationen -->
  <div
    ref="previewRoot"
    class="aspect-preview"
    :data-theme="thisVersion()"
    :class="background"
  >
    <div class="slide">
      <!-- Tom slide  -->
      <div v-if="slide.slideType === ''"></div>

      <!-- Huvudrubrik  -->
      <div v-else-if="slide.slideType === 'slideTitle'" class="flexer title-container">
        <div class="headline">
          <mark v-html="resolveSlideHtml(slide.title)"></mark>
        </div>
      </div>

      <!-- Rubrik  -->
      <div v-else-if="slide.slideType === 'slideHeadline'" class="flexer title-container">
        <div class="headline" v-html="resolveSlideHtml(slide.title)"></div>
      </div>

      <!-- Innehållsidor, Helsida och Delad sida delar samma renderingslogik -->
      <div
        v-else-if="['slideContent','slideFullContent','splitContent'].includes(slide.slideType)"
        class="flexer"
        :class="{
          'flexer-full': slide.slideType === 'slideFullContent',
          'flexer-split': slide.slideType === 'splitContent'
        }"
      >
        <div v-if="slide.slideType === 'slideContent'" class="tab-container">
          <div
            v-for="sub in slide.subSlides"
            v-if="sub.description"
            :key="sub.id"
            class="tab-items"
          >
            {{ sub.description }}
          </div>
        </div>

        <template v-for="boxIndex in boxIndexes">
        <div
          v-if="slide.subSlides && slide.subSlides[boxIndex]"
          :key="'preview-box-' + boxIndex"
          class="content-container"
          :class="{
            'content-container-full': slide.slideType === 'slideFullContent',
            'split-box': slide.slideType === 'splitContent'
          }"
        >
          <!-- Team / Bildblock: content-yta för Fliksidor/Split, utan för Helsida -->
          <div
            v-if="
              slide.subSlides[boxIndex].contentType === 'contentTeam' ||
                slide.subSlides[boxIndex].contentType === 'contentVisual'
            "
            :class="
              slide.slideType === 'slideFullContent'
                ? 'content-plain'
                : 'content'
            "
          >
            <Team :source="slide.subSlides[boxIndex]" />
          </div>

          <!-- Lista -->
          <div
            v-else-if="slide.subSlides[boxIndex].contentType === 'contentList'"
            class="content"
          >
            <div class="list-container">
              <div
                v-for="(listitem, idx) in slide.subSlides[boxIndex].items"
                :key="idx"
                v-if="listitem !== ''"
                class="list-item"
                v-html="resolveSlideHtml(listitem)"
              ></div>
            </div>
          </div>

          <!-- Text -->
          <div
            v-else-if="slide.subSlides[boxIndex].contentType === 'contentText'"
            class="content"
          >
            <div
              class="text-item"
              v-html="resolveSlideHtml(slide.subSlides[boxIndex].text)"
            ></div>
          </div>

          <!-- AI-insikter -->
          <div
            v-else-if="slide.subSlides[boxIndex].contentType === 'contentAiInsights'"
            class="content"
          >
            <AiInsights :subSlide="slide.subSlides[boxIndex]" />
          </div>

          <!-- Diagram (samma komponenter som i presentationen, statisk demo-data) -->
          <div
            v-else-if="slide.subSlides[boxIndex].contentType === 'contentChart'"
            class="content"
            data-preview-chart-box
            :data-preview-chart-index="boxIndex"
          >
            <div
              class="chart-container"
              :key="
                'preview-chart-' +
                  boxIndex +
                  '-' +
                  chartContainerWidth(boxIndex)
              "
            >
              <DynamicColumnChart
                v-if="
                  resolveChartType(slide.subSlides[boxIndex].chart) ===
                    'DynamicColumnChart'
                "
                :financialRatio="slide.subSlides[boxIndex].chart"
                dark="true"
                :profitloss="previewCompanyData.profitloss"
                :balance="previewCompanyData.balance"
                :cashflow="previewCompanyData.cashflow"
                :containerWidth="chartContainerWidth(boxIndex)"
                :currentLanguage="currentLanguage"
              />
              <DynamicLineChart
                v-else-if="
                  resolveChartType(slide.subSlides[boxIndex].chart) ===
                    'DynamicLineChart'
                "
                :financialRatio="slide.subSlides[boxIndex].chart"
                dark="true"
                :profitloss="previewCompanyData.profitloss"
                :balance="previewCompanyData.balance"
                :cashflow="previewCompanyData.cashflow"
                :containerWidth="chartContainerWidth(boxIndex)"
                :currentLanguage="currentLanguage"
              />
              <ProfitLoss
                v-else-if="
                  resolveChartType(slide.subSlides[boxIndex].chart) ===
                    'ProfitLoss'
                "
                dark="true"
                :profitloss="previewProfitLossSheet"
                :containerWidth="chartContainerWidth(boxIndex)"
                :currentLanguage="currentLanguage"
              />
            </div>
          </div>

          <!-- Tomt innehåll / fallback -->
          <div v-else class="content"></div>
        </div>
        </template>
      </div>

      <!-- Logotyp som i huvudpresentationen -->
      <img
        v-if="thisVersion() === 'gt' && slide.slideType !== 'slideFullContent' && slide.slideType !== 'splitContent'"
        src="/static/gt/image/gt_logo_outline_white.png"
        class="gt-logo"
      />
      <div
        v-else-if="thisVersion() === 'demo' && slide.slideType !== 'slideFullContent' && slide.slideType !== 'splitContent'"
        class="ac-logo"
      >
        analys<span>center.se</span>
      </div>
    </div>
  </div>
</template>

<script>
import deck from '@/store/modules/deck';
import Team from './Team.vue';
import AiInsights from './AiInsights.vue';
import DynamicColumnChart from '@/charts/DynamicColumnChart_.vue';
import DynamicLineChart from '@/charts/DynamicLineChart_.vue';
import ProfitLoss from '@/charts/ProfitLoss_.vue';
import { chartType as resolveChartType } from '@/components/simpleSlide/chartType';
import { resolveMentionHtml } from '@/components/simpleSlide/financialMentions';
import {
  PREVIEW_COMPANY_DATA,
  getPreviewProfitLossSheet,
} from '@/components/simpleSlide/previewChartFixture';

export default {
  name: 'Preview',
  props: ['slide'],

  components: {
    Team,
    AiInsights,
    DynamicColumnChart,
    DynamicLineChart,
    ProfitLoss,
  },

  data() {
    return {
      chartContainerWidths: {},
      previewContainerWidth: 0,
      chartResizeObserver: null,
    };
  },

  computed: {
    background() {
      return deck.Background;
    },
    previewCompanyData() {
      return PREVIEW_COMPANY_DATA;
    },
    previewProfitLossSheet() {
      return getPreviewProfitLossSheet();
    },
    currentLanguage() {
      return deck.Language;
    },
    boxIndexes() {
      if (!this.slide || !this.slide.subSlides) {
        return [0];
      }
      if (this.slide.slideType === 'splitContent') {
        return [0, 1];
      }
      return [0];
    },
  },

  watch: {
    slide: {
      handler() {
        this.$nextTick(() => {
          this.measurePreviewContainers();
          this.observePreviewCharts();
        });
      },
      deep: true,
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.measurePreviewContainers();
      this.observePreviewCharts();
    });
  },

  beforeDestroy() {
    if (this.chartResizeObserver) {
      this.chartResizeObserver.disconnect();
      this.chartResizeObserver = null;
    }
  },

  methods: {
    resolveChartType,

    resolveSlideHtml(text) {
      let data = null;
      const orgNr = deck.OrgNr;
      if (orgNr && orgNr !== '00' && deck.Data && deck.Data[orgNr]) {
        data = deck.Data[orgNr][deck.SieAggVersion] || deck.Data[orgNr][0] || null;
      }
      // Do not fall back to PREVIEW_COMPANY_DATA (chart fixture) — that would
      // show fake financials / missing overview for mentions.
      return resolveMentionHtml(text, data);
    },

    thisVersion() {
      if (process.env.VUE_APP_BRAND === 'GT') {
        return 'gt';
      }
      return 'demo';
    },

    splitChartWidthFallback() {
      return Math.max(1, Math.round(this.previewContainerWidth * 0.47));
    },

    chartContainerWidth(boxIndex) {
      const key = String(boxIndex);
      const measured = this.chartContainerWidths[key];
      if (measured && measured > 0) {
        return measured;
      }
      if (this.slide && this.slide.slideType === 'splitContent') {
        return this.splitChartWidthFallback();
      }
      return Math.max(1, this.previewContainerWidth);
    },

    measurePreviewContainers() {
      const root = this.$refs.previewRoot;
      if (!root || !(root instanceof HTMLElement)) {
        return;
      }
      if (root.clientWidth > 0) {
        this.previewContainerWidth = root.clientWidth;
      }
      const widths = { ...this.chartContainerWidths };
      const boxes = root.querySelectorAll('[data-preview-chart-box]');
      boxes.forEach((box) => {
        const boxIndex = box.getAttribute('data-preview-chart-index');
        if (boxIndex === null) {
          return;
        }
        const chartContainer = box.querySelector('.chart-container');
        const content = box.querySelector('.content');
        const el = chartContainer || content;
        if (el && el.clientWidth > 0) {
          widths[boxIndex] = el.clientWidth;
        }
      });
      this.chartContainerWidths = widths;
    },

    observePreviewCharts() {
      if (this.chartResizeObserver) {
        this.chartResizeObserver.disconnect();
      }
      const root = this.$refs.previewRoot;
      if (!root || !(root instanceof HTMLElement) || !window.ResizeObserver) {
        return;
      }
      this.chartResizeObserver = new ResizeObserver(() => {
        this.measurePreviewContainers();
      });
      this.chartResizeObserver.observe(root);
      root.querySelectorAll('[data-preview-chart-box]').forEach((box) => {
        this.chartResizeObserver.observe(box);
      });
    },
  },
};

</script>

<style lang="scss" scoped>
@import '@/assets/_slides.scss';

.aspect-preview {
  width: 100%;
}

.aspect-preview .slide {
  aspect-ratio: 16 / 9;
  container-type: inline-size;
}

.content-container-full {
  flex-basis: 100%;
  padding: 2.5%;
}

.content-plain {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

/* Synka bakgrund med valet i Preferences (deck.Background) */

/* Demo-versionen (coding.jpg) */
.aspect-preview[data-theme='demo'].dark .slide {
  background-color: col('colors.Dark');
  background-image: url('/static/common/image/coding.jpg');
  background-blend-mode: multiply;
}

.aspect-preview[data-theme='demo'].light .slide {
  background-color: col('colors.LightGrey');
  background-image: url('/static/common/image/coding.jpg');
  background-blend-mode: screen;
}

.aspect-preview[data-theme='demo'].default .slide {
  background-color: #666;
  background-image: url('/static/common/image/coding.jpg');
  background-blend-mode: multiply;
}

/* GT-versionen */
.aspect-preview[data-theme='gt'].dark .slide {
  background-image: linear-gradient(
    52.5deg,
    var(--coral),
    var(--corePurple) 35%,
    var(--darkPurple) 120%
  );
}

.aspect-preview[data-theme='gt'].dark {
  .gt-logo {
    filter: none;
  }
}

.aspect-preview[data-theme='gt'].light .slide {
  background-image: url('/static/gt/image/gt_light.png');
}

.aspect-preview[data-theme='gt'].default .slide {
  background-image: url('/static/gt/image/gt_image.jpg');
}

/* Ljusa teman: synka text- och logofärger med huvudpresentationen */

.aspect-preview[data-theme='demo'].light {
  .headline {
    color: col('colors.Primary');
    text-shadow: none;

    mark {
      box-shadow: none;
    }
  }

  .tab-items {
    box-shadow: none;
  }

  .content {
    box-shadow: none;
  }

  .ac-logo {
    text-shadow: none;
    color: col('colors.Secondary');

    span {
      color: col('colors.Black');
    }
  }
}

.aspect-preview[data-theme='gt'] {
  .gt-logo {
    position: absolute;
    width: calc(3 * var(--gridUnitWidth) + 1 * var(--marginUnit));
    bottom: var(--marginUnit);
    margin-left: var(--marginUnit);
    filter: drop-shadow(0 0 1cqw col('colors.Black'));
    z-index: 30;
  }
}

.aspect-preview[data-theme='gt'].light {
  .headline {
    color: col('colors.Primary');
    text-shadow: none;

    mark {
      color: col('colors.Primary');
      text-shadow: none;
    }
  }

  .subtitle {
    color: col('colors.Black');
    text-shadow: none;
  }

  .tab-items {
    box-shadow: 0 0 1cqw 0 col('colors.DarkGrey');
  }

  .content {
    box-shadow: 0 0 1cqw 0 col('colors.DarkGrey');
  }

  .gt-logo {
    filter: invert(100%);
  }
}

</style>
